import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { BASE_PATH, BASE_PLUS } from '../domain/routes';

function useNavigationPaths() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const regex = new RegExp(`^${BASE_PATH.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/?`);
  const [parentPath = BASE_PLUS, nestedPath = null, nestedModalPath = null] = pathname
    .replace(regex, '')
    .split('/')
    .filter(Boolean);

  const clearNestedPath = () => navigate(`${BASE_PATH}${parentPath}${search}`);
  const navigateToNestedPath = useCallback(
    (newNestedPath: string, replace = false) =>
      navigate(`${BASE_PATH}${parentPath}/${newNestedPath}${search}`, { replace: false }),
    [navigate, parentPath],
  );

  const updatePath = (newNestedPath: string, paramToRemove: string | null = null, replace: boolean = true) => {
    const basePath = `${BASE_PATH}${parentPath}${newNestedPath}`;

    if (paramToRemove) {
      searchParams.delete(paramToRemove);
      setSearchParams(searchParams, { replace });
    }
    navigate(`${basePath}${searchParams.toString().length > 0 ? `?${searchParams}` : ''}`, { replace });
  };

  const navigateToNestedModalPath = useCallback(
    (nestedModalPath: string, replace = false) =>
      navigate(`${BASE_PATH}${parentPath}/${nestedPath}/${nestedModalPath}${search}`, { replace: false }),
    [navigate, parentPath, nestedModalPath],
  );

  const navigateToBaseUrlPath = (baseurl: string = BASE_PATH, replace: boolean = true) => {
    const newPathName = pathname.replace(BASE_PATH, baseurl);
    navigate(`${newPathName}${search}`, { replace });
  };

  const isVisible = !!nestedPath;
  useEffect(() => {
    document.body.style.position = isVisible ? 'sticky' : 'static';
    document.body.style.overflow = isVisible ? 'hidden' : 'auto';
  }, [isVisible]);

  return {
    parentPath,
    nestedPath,
    nestedModalPath,
    clearNestedPath,
    navigateToNestedPath,
    navigateToNestedModalPath,
    navigateToBaseUrlPath,
    queryParams: searchParams,
    updatePath,
  };
}

export default useNavigationPaths;
